import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, forwardRef, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatDivider } from '@angular/material/divider';
import { MatListItem, MatNavList } from '@angular/material/list';
import { MatMenuTrigger } from '@angular/material/menu';
import { AuthenticationType } from '@core/auth/models/authentication-type.enum';
import { AUTH_SERVICE_TOKEN } from '@core/auth/models/iauth-service.model';
import { AuthActions } from '@core/auth/store';
import { EngineTheme, ThemeService } from '@core/layout/services/theme.service';
import { MenuItemDto, SystemClient, UILanguageDto } from '@core/services/api-clients';
import { NotificationService } from '@core/services/notification.service';
import { RemoveUserData, SetUserLanguage } from '@core/user/store/actions';
import { getCurrentUser, getUILanguages, getUserLanguage } from '@core/user/store/selectors';
import { NoEventWidgetDirective } from '@core/widgets/directives/no-event-widget.directive';
import { WidgetDirective } from '@core/widgets/directives/widget.directive';
import { IScriptRunnerService, SCRIPT_RUNNER_SERVICE } from '@core/widgets/models/iscript-runner.service';
import { environment } from '@env';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { EMPTY } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { MenuGroupItemsComponent } from '../shared/menu-group-items/menu-group-items.component';
import { MenuItemComponent } from '../shared/menu-item/menu-item.component';
import { SelectMenuItem } from '../store/actions';
import {
  getAboutIcon,
  getDarkModeIcon,
  getFilledUserMenuGroup,
  getLanguageIcon,
  getLogoutIcon,
  getSelectedMenuItemId,
} from '../store/selectors';
import { EngineAboutDialogComponent } from './engine-about-dialog/engine-about-dialog.component';
import { EngineVersionData } from './engine-about-dialog/engine-version-data';
import { LanguagePickerComponent } from './language-picker/language-picker.component';
import { ThemePickerComponent } from './theme-picker/theme-picker.component';

@Component({
  selector: 'app-user-menu',
  templateUrl: './user-menu.component.html',
  styleUrls: ['./user-menu.component.scss'],
  providers: [
    {
      provide: WidgetDirective,
      useExisting: forwardRef(() => UserMenuComponent),
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MenuGroupItemsComponent,
    MatDivider,
    MatNavList,
    MatListItem,
    MatMenuTrigger,
    MenuItemComponent,
    ThemePickerComponent,
    LanguagePickerComponent,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class UserMenuComponent extends NoEventWidgetDirective {
  private _store = inject(Store);
  private _dialog = inject(MatDialog);
  private _systemClient = inject(SystemClient);
  private _notificationService = inject(NotificationService);
  private _themeService = inject(ThemeService);

  private _authService = inject(AUTH_SERVICE_TOKEN);

  userMenuGroup$ = this._store.select(getFilledUserMenuGroup);
  selectedMenuItemId$ = this._store.select(getSelectedMenuItemId);
  languages$ = this._store.select(getUILanguages);
  currentUser$ = this._store.select(getCurrentUser);
  userLanguage$ = this._store.select(getUserLanguage);
  logoutIcon$ = this._store.select(getLogoutIcon);
  darkModeIcon$ = this._store.select(getDarkModeIcon);
  languageIcon$ = this._store.select(getLanguageIcon);
  aboutIcon$ = this._store.select(getAboutIcon);
  theme$ = this._themeService.getThemeAsync();

  constructor() {
    const parentWidget = inject(WidgetDirective, { optional: true, skipSelf: true });
    const scriptRunnerService = inject<IScriptRunnerService>(SCRIPT_RUNNER_SERVICE);

    super(parentWidget, scriptRunnerService);
  }

  onMenuItemClick(menuItem: MenuItemDto) {
    this._store.dispatch(new SelectMenuItem({ menuItem }));
  }

  selectLanguage(language: UILanguageDto) {
    this._store.dispatch(new SetUserLanguage({ language: language }));
  }

  changeTheme(theme: EngineTheme) {
    this._themeService.setTheme(theme);
  }

  isLogoutVisible() {
    return (
      this._authService.getAuthenticationType() == AuthenticationType.AzureB2C ||
      this._authService.getAuthenticationType() == AuthenticationType.AzureActiveDirectory
    );
  }

  logout() {
    this._store.dispatch(new AuthActions.Logout());
    this._store.dispatch(new RemoveUserData());
  }

  displayAboutInfo() {
    this._systemClient
      .getEngineVersion()
      .pipe(
        map((v) => {
          return <EngineVersionData>{
            metadataVersion: v.metadataVersion,
            solutionVersion: v.solutionVersion,
            assemblyVersion: v.assemblyVersion,
            frontendVersion: environment.version,
          };
        }),
        tap((version) => {
          this._dialog.open(EngineAboutDialogComponent, { data: version });
        }),
        catchError(() => {
          this._notificationService.error('System version information could not be retrieved.');
          return EMPTY;
        }),
      )
      .subscribe();
  }
}
